import useTranslation from '../../customHooks/useTranslation';
import { Sheet, SheetHeading } from './Sheet';

// A professional seal, the way an engineer stamps a drawing set.
export function Seal({ code, name, rotate, className = 'h-36 w-36 sm:h-40 sm:w-40' }: { code: string; name: string; rotate: number; className?: string }) {
	const id = `seal-${code}`;
	// Short names repeat the issuer so every ring reads with the same letter spacing.
	const ring = name.length < 32 ? `${name} · Red Hat · ` : `${name} · `;
	return (
		<svg viewBox='0 0 200 200' className={`shrink-0 ${className}`} style={{ transform: `rotate(${rotate}deg)` }} role='img' aria-label={`${code}: ${name}`}>
			<defs>
				<path id={id} d='M100 100 m-74 0 a74 74 0 1 1 148 0 a74 74 0 1 1 -148 0' />
			</defs>
			<circle cx='100' cy='100' r='96' fill='none' strokeWidth='2' className='stroke-redline' />
			<circle cx='100' cy='100' r='88' fill='none' strokeWidth='1' className='stroke-redline' />
			<circle cx='100' cy='100' r='56' fill='none' strokeWidth='1' className='stroke-redline' />
			<text fontSize='12' fontWeight='600' className='fill-redline uppercase' style={{ fontStretch: '72%' }}>
				<textPath href={`#${id}`} startOffset='0' textLength={452} lengthAdjust='spacing'>
					{ring}
				</textPath>
			</text>
			<text x='100' y='107' textAnchor='middle' fontSize='22' fontWeight='800' className='fill-redline' style={{ fontStretch: '100%' }}>
				{code}
			</text>
			<line x1='70' y1='120' x2='130' y2='120' strokeWidth='1' className='stroke-redline' />
		</svg>
	);
}

export default function Stack() {
	const t = useTranslation();
	const s = t.stack;

	return (
		<Sheet id='stack' code='A-301' title={t.nav.sheets.stack} className='drafting-grid'>
			<div className='mx-auto max-w-[1320px] px-4 py-20 sm:px-8 lg:py-28'>
				<div className='grid gap-x-12 gap-y-10 lg:grid-cols-12'>
					<div className='lg:col-span-7'>
						<SheetHeading id='stack'>{s.heading}</SheetHeading>
						<p className='mt-5 max-w-[56ch] text-lg leading-relaxed text-ink-soft'>{s.intro}</p>
					</div>
					<div className='lg:col-span-5'>
						<h3 className='lettering text-label text-ink-faint'>{s.seals.heading}</h3>
						<div className='mt-3 flex flex-wrap items-center gap-4'>
							<Seal code='RHCSA' name={s.seals.rhcsa} rotate={-7} />
							<Seal code='RHCE' name={s.seals.rhce} rotate={5} />
						</div>
						<p className='mt-3 text-sm text-ink-faint'>{s.seals.issuer}</p>
					</div>
				</div>

				<div className='mt-14 overflow-hidden border border-ink-line'>
					<table className='w-full border-collapse text-left'>
						<thead className='bg-sheet-deep'>
							<tr className='lettering text-label text-ink-faint'>
								<th scope='col' className='w-14 border-b border-r border-ink-line px-3 py-3 font-semibold sm:w-16'>
									{s.columns.item}
								</th>
								<th scope='col' className='border-b border-r border-ink-line px-3 py-3 font-semibold'>
									{s.columns.name}
								</th>
								<th scope='col' className='hidden border-b border-r border-ink-line px-3 py-3 font-semibold sm:table-cell'>
									{s.columns.role}
								</th>
								<th scope='col' className='hidden border-b border-ink-line px-3 py-3 font-semibold md:table-cell'>
									{s.columns.note}
								</th>
							</tr>
						</thead>
						<tbody>
							{s.rows.map((row, i) => (
								<tr key={row.name} className='group align-top transition-colors hover:bg-sheet-deep/70'>
									<td className='border-b border-r border-ink-line px-3 py-3 text-sm text-ink-faint group-last:border-b-0'>{String(i + 1).padStart(2, '0')}</td>
									<td className='border-b border-r border-ink-line px-3 py-3 group-last:border-b-0'>
										<span className='font-semibold text-ink'>{row.name}</span>
										<span className='mt-1 block text-sm text-ink-soft sm:hidden'>{row.role}</span>
										<span className='mt-1 block text-sm text-ink-faint md:hidden'>{row.note}</span>
									</td>
									<td className='hidden border-b border-r border-ink-line px-3 py-3 text-ink-soft group-last:border-b-0 sm:table-cell'>{row.role}</td>
									<td className='hidden border-b border-ink-line px-3 py-3 text-ink-soft group-last:border-b-0 md:table-cell'>{row.note}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<p className='mt-6 max-w-[72ch] leading-relaxed text-ink-soft'>
					<span className='lettering mr-3 text-label text-redline'>{s.polyglot.label}</span>
					{s.polyglot.text}
				</p>
			</div>
		</Sheet>
	);
}
